import React, { useCallback, useEffect } from 'react';

import { connect } from 'react-redux';
import { Button, Form } from 'react-bootstrap';
import { Drawer } from 'rsuite';

import { PageHeader, Pagination, DrawerSeparator, Loading } from '../../components';
import DataTable from '../../components/DataTable/DataTable';
import CreateRoleModal from './CreateRoleModal';

import useComponentState from '../../utils/useComponentState';
import { searchAdministrators } from '../../api/auth';
import { getSubMerchantsByMerchant } from '../../api/subMerchant';
import { createRole } from '../../constants/createRole';
import { ROLE_MERCHANT_ADMINISTRATOR, getRoleLabel } from '../../constants/roles';

import styles from './createUser.module.scss';

const CreateUser = (props) => {
  const [state, setState] = useComponentState({
    showCreateRoleModal: false,
    tableData: [],
    loading: false,
    error: '',
    currentPage: 0,
    totalPages: 0,
    email: '',
    merchantId: '',
    showDrawer: false,
    drawerData: null,
    drawerSubMerchants: [],
    drawerLoading: false,
    drawerError: '',
  });

  const { token, details } = props.auth.auth;

  const fetchAdministrators = useCallback(
    async (page, email, merchantId) => {
      setState({ loading: true, error: '' });
      try {
        const response = await searchAdministrators(token, {
          page,
          pageSize: createRole.pageSize,
          email,
          merchantId,
        });
        setState({
          tableData: response.list || [],
          totalPages: response.totalPageCount || 0,
        });
      } catch (err) {
        console.error('searchAdministrators failed:', err);
        setState({
          error: getRoleLabel(
            err?.message || createRole.messages.fetchAdministratorsFailed
          ),
          tableData: [],
          totalPages: 0,
        });
      }
      setState({ loading: false });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token]
  );

  useEffect(() => {
    fetchAdministrators(state.currentPage, state.email, state.merchantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentPage]);

  const handleSearch = () => {
    setState({ currentPage: 0 });
    fetchAdministrators(0, state.email, state.merchantId);
  };

  const handleClearFilters = () => {
    setState({ email: '', merchantId: '', currentPage: 0 });
    fetchAdministrators(0, '', '');
  };

  const handlePagination = (page) => {
    setState({ currentPage: page });
  };

  const fetchDrawerSubMerchants = async (merchantIds) => {
    setState({ drawerLoading: true, drawerError: '' });
    try {
      const results = await Promise.all(
        merchantIds.map((merchantId) =>
          getSubMerchantsByMerchant(merchantId, token)
        )
      );
      const merged = results.reduce(
        (acc, result) => acc.concat(result.subMerchants || []),
        []
      );
      const uniqueById = Array.from(
        new Map(
          merged.map((subMerchant) => [subMerchant.id, subMerchant])
        ).values()
      );
      setState({ drawerSubMerchants: uniqueById });
    } catch (err) {
      setState({
        drawerError: createRole.messages.subMerchantsLoadFailed,
        drawerSubMerchants: [],
      });
    }
    setState({ drawerLoading: false });
  };

  const handleOnClick = (id) => {
    const rowData = state.tableData.find((data) => data.id === id);
    const merchantIds = (rowData && rowData.merchantIds) || [];
    if (
      rowData &&
      rowData.role === ROLE_MERCHANT_ADMINISTRATOR &&
      merchantIds.length > 0
    ) {
      setState({
        showDrawer: true,
        drawerData: rowData,
        drawerSubMerchants: [],
        drawerError: '',
      });
      fetchDrawerSubMerchants(merchantIds);
    }
  };

  const handleDrawerOnHide = () => {
    setState({
      showDrawer: false,
      drawerData: null,
      drawerSubMerchants: [],
      drawerError: '',
    });
  };

  const handleUserCreated = () => {
    setState({ currentPage: 0 });
    fetchAdministrators(0, state.email, state.merchantId);
  };

  const renderSubMerchantFields = (subMerchant) => {
    const email =
      (subMerchant.merchantContactDetails &&
        subMerchant.merchantContactDetails[0] &&
        subMerchant.merchantContactDetails[0].email) ||
      '';

    const fields = [
      { label: 'ID', value: subMerchant.id },
      { label: 'Email', value: email },
      { label: 'Location', value: subMerchant.location },
    ];

    return fields.map(({ label, value }) => (
      <div className={styles.credential_row} key={label}>
        <span className={styles.credential_label}>{label}</span>
        <span className={styles.credential_value}>
          {value === null || value === undefined || value === ''
            ? 'N/A'
            : String(value)}
        </span>
      </div>
    ));
  };

  const renderDrawer = () => {
    const { drawerSubMerchants, drawerLoading, drawerError } = state;

    return (
      <div className={styles.drawer_root}>
        <DrawerSeparator text={createRole.titles.subMerchantDetails} />
        {drawerLoading ? (
          <Loading text='Loading' />
        ) : drawerError ? (
          <p>{drawerError}</p>
        ) : drawerSubMerchants.length === 0 ? (
          <p>{createRole.notFoundMessage}</p>
        ) : (
          drawerSubMerchants.map((subMerchant, index) => (
            <div className={styles.credentials_box} key={subMerchant.id}>
              {drawerSubMerchants.length > 1 && (
                <div className={styles.credential_row}>
                  <span className={styles.credential_label}>{`#${
                    index + 1
                  }`}</span>
                </div>
              )}
              {renderSubMerchantFields(subMerchant)}
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <div className={styles.root_div}>
      <PageHeader text={createRole.titles.createUser} />

      <Form
        className={styles.filter_form}
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch();
        }}
      >
        <Form.Control
          className={styles.filter_input}
          type='text'
          placeholder={createRole.placeholders.searchByEmail}
          value={state.email}
          onChange={(e) => setState({ email: e.target.value })}
        />
        <Form.Control
          className={styles.filter_input}
          type='text'
          placeholder={createRole.placeholders.searchByMerchantId}
          value={state.merchantId}
          onChange={(e) => setState({ merchantId: e.target.value })}
        />
        <Button type='submit'>{createRole.buttonText.search}</Button>
        <Button type='button' variant='secondary' onClick={handleClearFilters}>
          {createRole.buttonText.clear}
        </Button>
        <Button
          type='button'
          className={styles.create_role_btn}
          onClick={() => setState({ showCreateRoleModal: true })}
        >
          {createRole.buttonText.createRole}
        </Button>
      </Form>

      <DataTable
        tableData={state.tableData}
        tableheading={{ column: createRole.tableColumns }}
        handleOnClick={handleOnClick}
        tag={createRole.tag}
        fetchError={state.error}
        search_filter_loading={state.loading}
        notFoundMessage={createRole.notFoundMessage}
      />

      <Pagination
        pageCount={state.totalPages}
        onChange={handlePagination}
        currentPage={state.currentPage}
      />

      <CreateRoleModal
        show={state.showCreateRoleModal}
        onHide={() => setState({ showCreateRoleModal: false })}
        token={token}
        currentUserRole={details.role}
        onCreated={handleUserCreated}
      />

      <Drawer show={state.showDrawer} onHide={handleDrawerOnHide}>
        {state.drawerData && renderDrawer()}
      </Drawer>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  };
};

export default connect(mapStateToProps)(CreateUser);
